import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, BookOpen, Target, TrendingUp,
  Calculator, Languages, CheckCircle2, Clock
} from 'lucide-react';
import { client } from '../api/client';

interface Topic {
  id: number;
  subjectId: number;
  name: string;
  nameCn: string;
  grade: number;
  description?: string;
  isNaplan?: string;
  isVcePrep?: string;
  sortOrder: number;
}

interface Subject {
  id: number;
  name: string;
  nameCn: string;
}

const subjectConfig: Record<string, { icon: React.ReactNode; gradient: string; bg: string; text: string }> = {
  'mathematics': { 
    icon: <Calculator className="w-6 h-6" />, 
    gradient: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50',
    text: 'text-blue-600'
  },
  'english': { 
    icon: <Languages className="w-6 h-6" />, 
    gradient: 'from-emerald-500 to-teal-600',
    bg: 'bg-emerald-50',
    text: 'text-emerald-600'
  },
};

const grades = [
  { year: 7, label: 'Year 7' },
  { year: 8, label: 'Year 8' },
  { year: 9, label: 'Year 9' },
  { year: 10, label: 'Year 10' },
];

export function TopicsPage() {
  const { subject: subjectSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const gradeParam = searchParams.get('grade');
  
  const [subject, setSubject] = useState<Subject | null>(null);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<number>(
    gradeParam ? parseInt(gradeParam) : 7
  );
  const [loading, setLoading] = useState(true);

  const config = subjectConfig[subjectSlug || ''] || subjectConfig['mathematics'];

  useEffect(() => {
    loadData();
  }, [subjectSlug, selectedGrade]);

  async function loadData() {
    setLoading(true);
    try {
      // Load subjects to get the correct subject ID
      const subjectsRes = await client.api.fetch('/api/public/subjects');
      const subjectsData = await subjectsRes.json();
      const foundSubject = (subjectsData.data || []).find(
        (s: Subject) => s.name.toLowerCase() === subjectSlug?.toLowerCase()
      );
      
      if (foundSubject) {
        setSubject(foundSubject);
        
        // Load topics for this subject and grade
        const topicsRes = await client.api.fetch(
          `/api/public/topics?subject_id=${foundSubject.id}&grade=${selectedGrade}`
        );
        const topicsData = await topicsRes.json();
        setTopics(topicsData.data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleGradeChange(grade: number) {
    setSelectedGrade(grade);
    setSearchParams({ grade: grade.toString() });
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-8">
          <Link to="/subjects" className="hover:text-blue-600 transition-colors">
            题库中心
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-900 font-medium">{subject?.nameCn}</span>
        </nav>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-14 h-14 bg-gradient-to-br ${config.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
              {config.icon}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{subject?.nameCn}</h1>
              <p className="text-slate-600">选择知识点开始练习</p>
            </div>
          </div>
        </motion.div>

        {/* Grade Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-100 p-2 mb-8 inline-flex gap-1"
        >
          {grades.map((grade) => (
            <button
              key={grade.year}
              onClick={() => handleGradeChange(grade.year)}
              className={`px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                selectedGrade === grade.year
                  ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg`
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {grade.label}
            </button>
          ))}
        </motion.div>

        {/* Topics Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {topics.map((topic, index) => (
            <motion.div
              key={topic.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <Link
                to={`/practice/${topic.id}`}
                className="group block bg-white rounded-xl border border-slate-100 hover:border-slate-200 p-5 hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 ${config.bg} rounded-lg flex items-center justify-center ${config.text}`}>
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                  {topic.nameCn}
                </h3>
                <p className="text-sm text-slate-500 mb-3">{topic.name}</p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-1.5">
                  {topic.isNaplan === '1' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 rounded text-xs text-purple-600">
                      <Target className="w-3 h-3" />
                      NAPLAN
                    </span>
                  )}
                  {topic.isVcePrep === '1' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 rounded text-xs text-orange-600">
                      <TrendingUp className="w-3 h-3" />
                      VCE
                    </span>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {topics.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">暂无知识点</h3>
            <p className="text-slate-500 mb-6">该年级的知识点正在准备中</p>
            <Link
              to="/subjects"
              className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              返回科目列表
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
}
